/*
 * view-descent.ts - the descent of a pass that follows the view
 */

import type MapCamera from './camera';
import type MapSurfaceTile from './surface-tile';
import type MapMetanode from './metanode';
import type { TerrainDescent } from './draw-traversal';
import type { vec3 } from '../utils/math';


/**
 * The descent of the passes that draw what the selection camera sees.
 * A tile is culled when its box lies outside the frustum, and then
 * nothing of it is wanted. A tile is wanted finer while its texel size
 * is above the fit. Fallbacks fetch on the configured cadence.
 *
 * `wantsFiner` reads the tile's texel size, which the traversal updates
 * for every tile that passes the cull.
 */
export class ViewDescent implements TerrainDescent {

    /** @param selection the camera tiles are culled and sized against */
    constructor(selection: MapCamera) {

        this.eye = selection.geometry.eye;
    }

    culled(tile: MapSurfaceTile, node: MapMetanode): boolean {

        return !tile.bboxVisible(tile.id, this.eye, node);
    }

    /** A culled tile is off screen. */
    wantsCovered(_tile: MapSurfaceTile, _node: MapMetanode): boolean {

        return false;
    }

    /**
     * Geometry-bearing nodes use measured screen-space error. Geometry-less
     * nodes use the structural estimate against a fraction of the normal fit
     * threshold. A zero brake makes that threshold zero, so structural descent
     * remains unbounded.
     */
    wantsFiner(tile: MapSurfaceTile, _node: MapMetanode): boolean {

        const config = tile.map.config;

        if (tile.texelSize !== Infinity)
            return tile.texelSize > config.mapTexelSizeFit;

        return tile.fallbackTexelSize
            > config.mapTexelSizeFit * config.mapStructuralDescentBrake;
    }

    fallbackFetches(tile: MapSurfaceTile): boolean {

        return tile.id[0] % tile.map.config.mapFallbackCadence === 0;
    }

    private readonly eye: vec3;
}

export default ViewDescent;
